import { useState, useEffect } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Btn } from "@/app/ui";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { useFetch } from "@/lib/useFetch";
import { PLAN_META } from "@/lib/format";
import { Check, CreditCard, Globe, AlertTriangle, FileText, ExternalLink, Download } from "lucide-react";

const PLAN_DESC = { free: "CRM, quotes & invoices.", basic: "Adds sending, VAT, jobs, planner, expenses, reports & Alfie.", full: "Adds team, time clock, materials & job margins." };
const BUILD_LABEL = { new: "Requested", in_progress: "In progress", on_hold: "On hold", done: "Live" };

export default function Settings() {
  const { user, refetch } = useAuth();
  const [biz, setBiz] = useState(user?.business || {});
  const [saving, setSaving] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [addonLoading, setAddonLoading] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonNote, setReasonNote] = useState("");
  useEffect(() => { setBiz(user?.business || {}); }, [user]);

  const set = (k) => (e) => setBiz({ ...biz, [k]: e.target.value });
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/business", {
        name: biz.name, email: biz.email, phone: biz.phone, address: biz.address,
        vat_number: biz.vat_number,
        currency: biz.currency, quote_prefix: biz.quote_prefix, invoice_prefix: biz.invoice_prefix,
      });
      await refetch(); toast.success("Settings saved");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const changePlan = async (plan) => {
    if (plan === "free") { setReason(""); setReasonNote(""); setCancelOpen(true); return; }
    try {
      const { data } = await api.post("/billing/checkout", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const confirmDowngrade = async () => {
    const fullReason = [reason, reasonNote].filter(Boolean).join(" — ");
    try {
      await api.post("/business/plan", { plan: "free", reason: fullReason || undefined });
      await refetch(); await refetchSummary(); setCancelOpen(false);
      toast.success("Switched to Free plan");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const manageCard = async () => {
    setPortalLoading(true);
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); window.location.href = data.url; }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); setPortalLoading(false); }
  };
  const toggleAddon = async (enabled) => {
    setAddonLoading(true);
    try {
      await api.post("/billing/website-addon", { enabled });
      await refetchSummary(); await refetch();
      toast.success(enabled ? "Website add-on enabled" : "Website add-on removed");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setAddonLoading(false);
  };
  const fmtEpoch = (s) => s ? new Date(s * 1000).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }) : "—";

  const current = user?.business?.plan || "free";
  const { data: pricing } = useFetch("/public/pricing");
  const { data: summary, refetch: refetchSummary } = useFetch("/billing/summary");
  const { data: invoicesData } = useFetch("/billing/invoices");
  const cur = pricing?.currency || "€";
  const field = (l, k, type = "text") => (
    <div><label className="text-sm font-medium text-slate-700">{l}</label>
      <input type={type} value={biz[k] || ""} onChange={set(k)} data-testid={`settings-${k}`} className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 focus:ring-2 focus:ring-blue-500 outline-none" /></div>
  );

  return (
    <div className="animate-fade-up">
      <PageHeader title="Settings" description="Manage your business profile, VAT, numbering and plan." />
      <Tabs defaultValue="business">
        <TabsList className="mb-4">
          <TabsTrigger value="business" data-testid="settings-tab-business">Business</TabsTrigger>
          <TabsTrigger value="vat" data-testid="settings-tab-vat">VAT & Numbering</TabsTrigger>
          <TabsTrigger value="plan" data-testid="settings-tab-plan">Plan</TabsTrigger>
        </TabsList>

        <TabsContent value="business">
          <div className="amden-card p-6 space-y-3 max-w-2xl">
            {field("Business name", "name")}
            <div className="grid sm:grid-cols-2 gap-3">{field("Email", "email")}{field("Phone", "phone")}</div>
            {field("Address", "address")}
            <Btn variant="accent" onClick={save} disabled={saving} data-testid="settings-save-business">{saving ? "Saving…" : "Save changes"}</Btn>
          </div>
        </TabsContent>

        <TabsContent value="vat">
          <div className="amden-card p-6 space-y-3 max-w-2xl">
            <div className="p-3 rounded-lg border border-slate-200 bg-slate-50">
              <p className="font-medium text-navy">VAT is charged at the standard 23% rate</p>
              <p className="text-sm text-slate-500">VAT is a regulatory charge applied to all quotes &amp; invoices. The rate is fixed at 23% and can't be changed.</p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {field("VAT number", "vat_number")}
              <div>
                <label className="text-sm font-medium text-slate-700">VAT rate (%)</label>
                <input type="text" value="23" readOnly disabled data-testid="settings-vat-rate-fixed" className="mt-1 w-full rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-slate-500 cursor-not-allowed outline-none" />
              </div>
            </div>
            <div className="grid sm:grid-cols-3 gap-3">{field("Currency", "currency")}{field("Quote prefix", "quote_prefix")}{field("Invoice prefix", "invoice_prefix")}</div>
            <Btn variant="accent" onClick={save} disabled={saving} data-testid="settings-save-vat">{saving ? "Saving…" : "Save changes"}</Btn>
          </div>
        </TabsContent>

        <TabsContent value="plan">
          {summary && (
            <div className="amden-card p-6 mb-6 max-w-3xl" data-testid="billing-summary">
              {summary.past_due && (
                <div className="mb-4 flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" data-testid="billing-pastdue">
                  <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                  <div>Your last payment failed. Please update your card to keep your workspace active. <button onClick={manageCard} className="underline font-semibold">Update card</button></div>
                </div>
              )}
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Current plan</p>
                  <p className="text-lg font-bold text-navy">{PLAN_META[summary.plan]?.name} · {summary.currency}{summary.plan_price ?? 0}/mo</p>
                  {summary.subscription?.current_period_end && (
                    <p className="text-sm text-slate-500 mt-0.5" data-testid="billing-next-charge">{summary.subscription.cancel_at_period_end ? "Ends" : (summary.subscription.status === "trialing" ? "Trial ends" : "Next charge")} {fmtEpoch(summary.subscription.current_period_end)}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">Card on file</p>
                  {summary.card ? (
                    <p className="text-sm font-medium text-navy flex items-center gap-2 justify-end" data-testid="billing-card"><CreditCard size={15} /> {summary.card.brand?.toUpperCase()}{summary.card.last4 ? ` •••• ${summary.card.last4}` : ""}{summary.card.exp_month ? ` · ${String(summary.card.exp_month).padStart(2, "0")}/${String(summary.card.exp_year).slice(-2)}` : ""}</p>
                  ) : <p className="text-sm text-slate-400">No card saved</p>}
                  <button onClick={manageCard} disabled={portalLoading} data-testid="billing-manage-card" className="mt-2 px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50 disabled:opacity-50 transition-colors">{portalLoading ? "Opening…" : "Manage card"}</button>
                </div>
              </div>
              <div className="mt-5 pt-5 border-t border-slate-100 flex items-center justify-between gap-4">
                <div className="flex items-start gap-3">
                  <span className="h-9 w-9 rounded-lg bg-blue-50 text-blue-accent flex items-center justify-center shrink-0"><Globe size={18} /></span>
                  <div>
                    <p className="font-medium text-navy">Website add-on</p>
                    <p className="text-sm text-slate-500">A professional website for your business — {summary.currency}{summary.website_addon.price}/mo added to your subscription.</p>
                    {!summary.website_addon_available && <p className="text-xs text-amber-600 mt-1">Available on Basic or Full plans.</p>}
                    {summary.website_addon.enabled && summary.website_addon.build_status && <p className="text-xs font-medium mt-1" data-testid="website-build-status">Website status: <span className="text-blue-accent">{BUILD_LABEL[summary.website_addon.build_status]}</span></p>}
                  </div>
                </div>
                <button onClick={() => toggleAddon(!summary.website_addon.enabled)} disabled={!summary.website_addon_available || addonLoading} data-testid="billing-website-addon-toggle"
                  className={`h-6 w-11 rounded-full transition-colors shrink-0 disabled:opacity-40 ${summary.website_addon.enabled ? "bg-blue-600" : "bg-slate-300"}`}>
                  <span className={`block h-5 w-5 bg-white rounded-full transition-transform ${summary.website_addon.enabled ? "translate-x-5" : "translate-x-0.5"}`} />
                </button>
              </div>
            </div>
          )}
          {invoicesData?.invoices?.length > 0 && (
            <div className="amden-card p-6 mb-6 max-w-3xl" data-testid="billing-invoices">
              <h3 className="font-semibold text-navy mb-3 flex items-center gap-2"><FileText size={16} /> Invoice history</h3>
              <div className="divide-y divide-slate-100">
                {invoicesData.invoices.map((inv, i) => (
                  <div key={i} className="flex items-center justify-between py-2.5 text-sm" data-testid={`billing-invoice-${i}`}>
                    <div>
                      <p className="font-medium text-navy">{fmtEpoch(inv.created)}</p>
                      <p className="text-xs text-slate-400">{inv.number || "—"} · <span className="capitalize">{inv.status}</span></p>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-mono font-semibold text-navy">{inv.currency === "EUR" ? "€" : inv.currency + " "}{Number(inv.amount).toFixed(2)}</span>
                      {inv.hosted_invoice_url && <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-blue-accent inline-flex items-center gap-1 hover:underline" data-testid={`billing-invoice-view-${i}`}>View <ExternalLink size={13} /></a>}
                      {inv.invoice_pdf && <a href={inv.invoice_pdf} target="_blank" rel="noreferrer" className="text-slate-500 inline-flex items-center gap-1 hover:underline" data-testid={`billing-invoice-pdf-${i}`}>PDF <Download size={13} /></a>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-3">
            {["free", "basic", "full"].map((key) => {
              const p = pricing?.plans?.[key];
              return (
              <div key={key} className={`amden-card p-6 ${current === key ? "ring-2 ring-blue-600" : ""}`} data-testid={`settings-plan-${key}`}>
                <div className="flex items-center justify-between"><h3 className="font-semibold text-navy">{PLAN_META[key].name}</h3>{current === key && <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 flex items-center gap-1"><Check size={12} /> Current</span>}</div>
                <p className="font-mono text-2xl font-bold text-navy mt-2">{cur}{p ? p.price : "—"}<span className="text-sm text-slate-400 font-sans">/mo</span></p>
                <p className="text-sm text-slate-500 mt-1">{PLAN_DESC[key]}</p>
                {current !== key && <Btn variant="accent" className="w-full mt-4" onClick={() => changePlan(key)} data-testid={`settings-choose-${key}`}>Switch to {PLAN_META[key].name}</Btn>}
              </div>
              );
            })}
          </div>
          <p className="text-sm text-slate-400 mt-4">All prices monthly · {pricing?.vat_inclusive ? "VAT included" : "VAT excluded"}. Website add-on +{cur}{pricing?.website_addon_price ?? 15}/mo (Basic or Full). Upgrades go through secure Stripe checkout; downgrading to Free keeps your card on file.</p>
        </TabsContent>
      </Tabs>
      <Dialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <DialogContent data-testid="cancel-feedback-dialog">
          <DialogHeader><DialogTitle>Switching to Free</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500">Your card stays saved and your paid subscription is cancelled. Mind sharing why you're downgrading? (optional)</p>
          <div className="space-y-3 mt-2">
            <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cancel-reason-select" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm">
              <option value="">Select a reason…</option>
              <option>Too expensive</option>
              <option>Not using it enough</option>
              <option>Missing features I need</option>
              <option>Switching to another tool</option>
              <option>Just testing</option>
              <option>Other</option>
            </select>
            <textarea value={reasonNote} onChange={(e) => setReasonNote(e.target.value)} data-testid="cancel-reason-note" placeholder="Anything else? (optional)" rows={3} className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm" />
          </div>
          <DialogFooter>
            <button onClick={() => setCancelOpen(false)} className="px-4 py-2 rounded-lg border border-slate-300 text-sm font-medium hover:bg-slate-50">Keep my plan</button>
            <button onClick={confirmDowngrade} data-testid="cancel-confirm" className="px-4 py-2 rounded-lg bg-navy text-white text-sm font-semibold hover:opacity-90">Switch to Free</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
